import LoadingButton from '@mui/lab/LoadingButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ConfirmationDialog } from '../../../../components/AsyncDialog';
import { AsyncDialog, AsyncDialogRef } from '../../../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialogData } from '../../../../components/AsyncDialog/ConfirmationDialog';
import { ProtectedContent } from '../../../../components/ProtectedContent';
import { CARD_BORDER } from '../../../../components/Section';
import COLORS from '../../../../constants/colors';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import { useAppDispatch } from '../../../../redux/hooks';
import {
  mockRedemptionBank,
  simulateNtaSubscription,
  triggerJournalStatus,
  triggerTradeEvent,
  triggerTransferStatus,
} from '../../../../redux/modules/alpaca/alpaca.actions';
import { alpacaSelector } from '../../../../redux/modules/alpaca/alpaca.selectors';
import { AlpacaOrderSide, AlpacaOrderStep, IAlpacaOrder } from '../../../../types/alpaca';
import * as permissions from '../../../../utils/permissions';

interface AlpacaMockActionsProps {
  order: IAlpacaOrder;
  refresh: () => void;
}

const JOURNAL_STATUS_OPTIONS = [
  { value: 'executed', label: 'Executed' },
  { value: 'correct', label: 'Correct' },
];

/** Controls sit on one wrapping row; no Grid spacing, so no negative margins. */
const mockControlsSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '12px',
};

/** Sub-section label inside the mocks card. */
const mockLabelSx = {
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: COLORS.LIGHT_GRAY,
};

interface MockRowProps {
  label: string;
  children: React.ReactNode;
}

/** One labelled group of mock controls, separated from the next by a rule. */
function MockRow({ label, children }: MockRowProps) {
  return (
    <Box
      sx={{
        pt: '16px',
        mt: '16px',
        borderTop: `1px solid ${CARD_BORDER}`,
        '&:first-of-type': { pt: 0, mt: 0, borderTop: 'none' },
      }}
    >
      <Typography sx={mockLabelSx}>{label}</Typography>
      {children}
    </Box>
  );
}

export function AlpacaMockActions({ order, refresh }: AlpacaMockActionsProps) {
  const { t } = useTranslation('apiError');
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const alpacaState = useSelector(alpacaSelector);
  const redemptionDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);

  const [ntaNetAmountUsd, setNtaNetAmountUsd] = useState<string>('');
  const [journalStatusTo, setJournalStatusTo] = useState<string>('executed');
  const [journalNetAmount, setJournalNetAmount] = useState<string>('');
  const [transferAmountUsd, setTransferAmountUsd] = useState<string>('');
  const [transferStatusTo, setTransferStatusTo] = useState<string>('COMPLETE');
  const [redemptionAmountSar, setRedemptionAmountSar] = useState<string>('');
  const [tradeFilledQty, setTradeFilledQty] = useState<string>('');
  const [tradeFilledAvgPrice, setTradeFilledAvgPrice] = useState<string>('');
  const [tradeNotionalUsd, setTradeNotionalUsd] = useState<string>('');

  const showError = (label: string, err: any) =>
    enqueueSnackbar(`${label}: ${t(err?.message ?? '')} ${JSON.stringify(err?.data ?? {})}`, { variant: 'error' });
  const showSuccess = (label: string) => {
    enqueueSnackbar(`${label} succeeded`, { variant: 'success' });
    refresh();
  };

  const handleNtaSubscription = async () => {
    const netAmountUsd = Number(ntaNetAmountUsd);
    if (Number.isNaN(netAmountUsd) || order.alpacaTransfer?.id == null) return;
    try {
      await dispatch(simulateNtaSubscription({ transferId: order.alpacaTransfer.id, netAmountUsd })).unwrap();
      showSuccess('NTA subscription');
    } catch (err: any) {
      showError('NTA subscription', err);
    }
  };

  const handleJournalStatus = async () => {
    if (!order.alpacaJournal?.journalId) return;
    const body: { journalId: string; statusTo?: string; netAmount?: number } = {
      journalId: order.alpacaJournal.journalId,
    };
    if (journalStatusTo) body.statusTo = journalStatusTo;
    const netAmount = Number(journalNetAmount);
    if (!Number.isNaN(netAmount)) body.netAmount = netAmount;
    try {
      await dispatch(triggerJournalStatus(body)).unwrap();
      showSuccess('Journal status');
    } catch (err: any) {
      showError('Journal status', err);
    }
  };

  const handleTransferStatus = async () => {
    const amountUsd = Number(transferAmountUsd);
    if (Number.isNaN(amountUsd) || order.alpacaTransfer?.id == null) return;
    try {
      await dispatch(
        triggerTransferStatus({
          transferId: order.alpacaTransfer.id,
          amountUsd,
          statusTo: transferStatusTo || undefined,
        }),
      ).unwrap();
      showSuccess('Transfer status');
    } catch (err: any) {
      showError('Transfer status', err);
    }
  };

  const handleRedemptionBank = async () => {
    const amountSar = Number(redemptionAmountSar);
    if (Number.isNaN(amountSar) || order.alpacaTransfer?.id == null) return;
    const confirmed = await redemptionDialogRef.current?.show({
      title: 'Mock Redemption Bank',
      description: `Simulate outgoing redemption transfer: ${amountSar} SAR for transfer ${order.alpacaTransfer.id}. Continue?`,
    });
    if (!confirmed) return;
    try {
      await dispatch(mockRedemptionBank({ transferId: order.alpacaTransfer.id, amountSar })).unwrap();
      showSuccess('Redemption bank mock');
    } catch (err: any) {
      showError('Redemption bank mock', err);
    }
  };

  const handleTradeEvent = async () => {
    const body: {
      orderId: number;
      filledQty?: number;
      filledAvgPrice?: number;
      notionalUsd?: number;
    } = { orderId: order.id };
    const filledQty = Number(tradeFilledQty);
    if (!Number.isNaN(filledQty)) body.filledQty = filledQty;
    const filledAvgPrice = Number(tradeFilledAvgPrice);
    if (!Number.isNaN(filledAvgPrice)) body.filledAvgPrice = filledAvgPrice;
    const notionalUsd = Number(tradeNotionalUsd);
    if (!Number.isNaN(notionalUsd)) body.notionalUsd = notionalUsd;
    try {
      await dispatch(triggerTradeEvent(body)).unwrap();
      showSuccess('Trade event');
    } catch (err: any) {
      showError('Trade event', err);
    }
  };

  const hasTransfer = order.alpacaTransfer?.id != null;
  const hasJournal = order.alpacaJournal?.journalId != null;
  const isSell = order.side === AlpacaOrderSide.SELL;
  const isBuy = order.side === AlpacaOrderSide.BUY;
  // NTA subscription is for incoming (subscription) transfer settlement only; not used in redemption
  const showNta = hasTransfer && isBuy;
  // Backend only accepts trigger-trade-event when order.step === ORDER_CREATED
  const showTradeEvent =
    order.alpacaOrderId != null && order.alpacaJournal != null && order.step === AlpacaOrderStep.ORDER_CREATED;

  const ntaLoading = alpacaState.simulateNtaSubscriptionStatus === fetchStatuses.pending;
  const journalLoading = alpacaState.triggerJournalStatusStatus === fetchStatuses.pending;
  const transferLoading = alpacaState.triggerTransferStatusStatus === fetchStatuses.pending;
  const redemptionLoading = alpacaState.mockRedemptionBankStatus === fetchStatuses.pending;
  const tradeLoading = alpacaState.triggerTradeEventStatus === fetchStatuses.pending;

  return (
    <>
      <Box sx={{ pt: '16px', mt: '16px', borderTop: `1px solid ${CARD_BORDER}` }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: COLORS.MAIN_DARK }}>
          Manual Alpaca Mocks
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: COLORS.LIGHT_GRAY, mt: '2px' }}>
          Development-only simulation of Alpaca callbacks
        </Typography>
      </Box>
      <Box sx={{ mt: '16px' }}>
        {showNta && (
          <MockRow label="NTA subscription">
            <ProtectedContent items={[permissions.AlpacaPermissions.sendToTransferQueue]}>
              <Box sx={mockControlsSx}>
                <TextField
                  size="small"
                  label="Net amount USD"
                  type="number"
                  value={ntaNetAmountUsd}
                  onChange={(e) => setNtaNetAmountUsd(e.target.value)}
                  inputProps={{ step: 0.01 }}
                />
                <LoadingButton
                  variant="outlined"
                  loading={ntaLoading}
                  onClick={handleNtaSubscription}
                  disabled={!ntaNetAmountUsd.trim()}
                >
                  Simulate NTA subscription
                </LoadingButton>
              </Box>
            </ProtectedContent>
          </MockRow>
        )}

        {hasJournal && (
          <MockRow label="Journal status">
            <ProtectedContent items={[permissions.AlpacaPermissions.sendToJournalQueue]}>
              <Box sx={mockControlsSx}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status to</InputLabel>
                  <Select
                    value={journalStatusTo}
                    label="Status to"
                    onChange={(e) => setJournalStatusTo(e.target.value)}
                  >
                    {JOURNAL_STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Net amount (optional)"
                  type="number"
                  value={journalNetAmount}
                  onChange={(e) => setJournalNetAmount(e.target.value)}
                  inputProps={{ step: 0.01 }}
                />
                <LoadingButton variant="outlined" loading={journalLoading} onClick={handleJournalStatus}>
                  Trigger journal status
                </LoadingButton>
              </Box>
            </ProtectedContent>
          </MockRow>
        )}

        {hasTransfer && (
          <MockRow label="Transfer status">
            <ProtectedContent items={[permissions.AlpacaPermissions.sendToTransferQueue]}>
              <Box sx={mockControlsSx}>
                <TextField
                  size="small"
                  label="Amount USD"
                  type="number"
                  required
                  value={transferAmountUsd}
                  onChange={(e) => setTransferAmountUsd(e.target.value)}
                  inputProps={{ step: 0.01 }}
                />
                <TextField
                  size="small"
                  label="Status to (optional)"
                  value={transferStatusTo}
                  onChange={(e) => setTransferStatusTo(e.target.value)}
                />
                <LoadingButton
                  variant="outlined"
                  loading={transferLoading}
                  onClick={handleTransferStatus}
                  disabled={!transferAmountUsd.trim()}
                >
                  Trigger transfer status
                </LoadingButton>
              </Box>
            </ProtectedContent>
          </MockRow>
        )}

        {isSell && hasTransfer && (
          <MockRow label="Redemption bank mock">
            <ProtectedContent items={[permissions.AlpacaPermissions.sendToCompleteQueue]}>
              <Box sx={mockControlsSx}>
                <TextField
                  size="small"
                  label="Amount SAR"
                  type="number"
                  value={redemptionAmountSar}
                  onChange={(e) => setRedemptionAmountSar(e.target.value)}
                  inputProps={{ step: 0.01 }}
                />
                <LoadingButton
                  variant="outlined"
                  color="warning"
                  loading={redemptionLoading}
                  onClick={handleRedemptionBank}
                  disabled={!redemptionAmountSar.trim()}
                >
                  Mock redemption bank
                </LoadingButton>
              </Box>
            </ProtectedContent>
          </MockRow>
        )}

        {showTradeEvent && (
          <MockRow label="Trade event">
            <ProtectedContent items={[permissions.AlpacaPermissions.sendToOrderQueue]}>
              <Box sx={mockControlsSx}>
                <TextField
                  size="small"
                  label="Filled qty (optional)"
                  type="number"
                  value={tradeFilledQty}
                  onChange={(e) => setTradeFilledQty(e.target.value)}
                  inputProps={{ step: 0.01 }}
                />
                <TextField
                  size="small"
                  label="Filled avg price (optional)"
                  type="number"
                  value={tradeFilledAvgPrice}
                  onChange={(e) => setTradeFilledAvgPrice(e.target.value)}
                  inputProps={{ step: 0.01 }}
                />
                <TextField
                  size="small"
                  label="Notional USD (optional)"
                  type="number"
                  value={tradeNotionalUsd}
                  onChange={(e) => setTradeNotionalUsd(e.target.value)}
                  inputProps={{ step: 0.01 }}
                />
                <LoadingButton variant="outlined" loading={tradeLoading} onClick={handleTradeEvent}>
                  Trigger trade event
                </LoadingButton>
              </Box>
            </ProtectedContent>
          </MockRow>
        )}
      </Box>
      <AsyncDialog ref={redemptionDialogRef} dialog={ConfirmationDialog} />
    </>
  );
}
